import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../utils/appConfirm';
import {
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiEdit2,
  FiEdit3,
  FiEye,
  FiLayers,
  FiPlus,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import '../styles/Contract.css';
import ContractForm from './ContractForm.js';
import ContractViewModal from './ContractViewModal.js';
import { formatAUD } from '../utils/formatCurrency';
import { graphqlRequest } from '../utils/graphqlRequest';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'Draft', label: 'Draft' },
  { key: 'Pending Signature', label: 'Pending Signature' },
  { key: 'Change Requested', label: 'Change Requested' },
  { key: 'Active', label: 'Active' },
  { key: 'EXPIRING', label: 'Expiring Soon' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Declined', label: 'Declined' },
  { key: 'Terminated', label: 'Terminated' },
];

const STATUS_CLASS = {
  Active: 'con-status--active',
  'Pending Signature': 'con-status--pending',
  Draft: 'con-status--draft',
  Completed: 'con-status--completed',
  Terminated: 'con-status--terminated',
  'Change Requested': 'con-status--changes',
  Declined: 'con-status--terminated',
};

const STATUS_TO_API = {
  Draft: 'DRAFT',
  'Pending Signature': 'PENDING_SIGNATURE',
  Active: 'ACTIVE',
  Completed: 'COMPLETED',
  Terminated: 'TERMINATED',
  'Change Requested': 'CHANGE_REQUESTED',
  Declined: 'DECLINED',
};

const STATUS_FROM_API = {
  DRAFT: 'Draft',
  PENDING_SIGNATURE: 'Pending Signature',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  TERMINATED: 'Terminated',
  CHANGE_REQUESTED: 'Change Requested',
  DECLINED: 'Declined',
};

const EXPIRY_WINDOW_DAYS = 30;

const CLIENT_CONTRACT_FIELDS = `
  id
  contractNumber
  proposalNumber
  bookingId
  quotationVersionId
  quotationNumber
  versionNumber
  hasActiveVersion
  title
  clientName
  clientEmail
  companyName
  siteAddress
  contactPerson
  billingAddress
  phoneNumber
  customerType
  serviceType
  frequency
  siteSize
  staffRequired
  serviceCost
  gstAmount
  additionalCharges
  discount
  value
  paymentReceived
  paymentTerms
  status
  scopeOfWork
  services
  terms
  notes
  contractDate
  startDate
  endDate
  validUntil
  sentAt
  signedAt
  signedName
  signedPosition
  signatureImage
  signatureText
  rejectReason
`;

const FIND_ALL_CLIENT_CONTRACTS = `
  query FindAllClientContracts {
    findAllClientContracts {
      ${CLIENT_CONTRACT_FIELDS}
    }
  }
`;

const FIND_ONE_CLIENT_CONTRACT = `
  query FindOneClientContract($id: String!) {
    findOneClientContract(id: $id) {
      ${CLIENT_CONTRACT_FIELDS}
    }
  }
`;

const CREATE_CLIENT_CONTRACT = `
  mutation CreateClientContract($input: CreateClientContractInput!) {
    createClientContract(input: $input) {
      id
      contractNumber
      status
      clientEmail
    }
  }
`;

const UPDATE_CLIENT_CONTRACT = `
  mutation UpdateClientContract($input: UpdateClientContractInput!) {
    updateClientContract(input: $input) {
      id
      contractNumber
      status
      clientEmail
    }
  }
`;

const REMOVE_CLIENT_CONTRACT = `
  mutation RemoveClientContract($id: String!) {
    removeClientContract(id: $id)
  }
`;

function parseMoney(value) {
  if (typeof value === 'number') return value;
  const num = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num : 0;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function daysUntil(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function isExpiringSoon(contract) {
  if (contract.status === 'Completed' || contract.status === 'Terminated') {
    return false;
  }
  const days = daysUntil(contract.dueDate);
  return days != null && days >= 0 && days <= EXPIRY_WINDOW_DAYS;
}

function familyLooksActive(contract) {
  return contract.status === 'Active' || Boolean(contract.hasActiveVersion);
}

function listStatusLabel(contract) {
  if (
    contract.hasActiveVersion &&
    contract.status !== 'Active' &&
    contract.status !== 'Completed' &&
    contract.status !== 'Terminated'
  ) {
    return `Active · ${contract.status}`;
  }
  return contract.status;
}

function matchesStatusTab(contract, tab) {
  if (tab === 'ALL') return true;
  if (tab === 'EXPIRING') return isExpiringSoon(contract);
  if (tab === 'Active') return familyLooksActive(contract);
  if (tab === 'Draft') return contract.status === 'Draft' && !contract.hasActiveVersion;
  return contract.status === tab;
}

function mapContractFromApi(row) {
  if (!row) return null;
  return {
    id: row.id,
    contractId: row.contractNumber,
    proposalNumber: row.proposalNumber || '',
    bookingId: row.bookingId || '',
    quotationVersionId: row.quotationVersionId || '',
    quotationNumber: row.quotationNumber || '',
    versionNumber: Number(row.versionNumber) || 1,
    hasActiveVersion: Boolean(row.hasActiveVersion),
    title: row.title || '',
    client: row.clientName || 'New Client',
    clientEmail: row.clientEmail || '',
    companyName: row.companyName || '',
    siteAddress: row.siteAddress || '',
    contactPerson: row.contactPerson || '',
    billingAddress: row.billingAddress || '',
    phoneNumber: row.phoneNumber || '',
    customerType: row.customerType || '',
    // List "Service" column should follow the editable title when present.
    service: row.title || row.serviceType || 'Untitled',
    serviceType: row.serviceType || '',
    frequency: row.frequency || '',
    siteSize: row.siteSize || '',
    staffRequired: row.staffRequired != null ? String(row.staffRequired) : '',
    serviceCost: row.serviceCost ?? '',
    gstAmount: row.gstAmount ?? '',
    additionalCharges: row.additionalCharges ?? '',
    discount: row.discount ?? '',
    billDate: row.startDate || '',
    dueDate: row.endDate || row.validUntil || '',
    contractDate: row.contractDate || '',
    startDate: row.startDate || '',
    endDate: row.endDate || '',
    validUntil: row.validUntil || '',
    total: Number(row.value) || 0,
    received: Number(row.paymentReceived) || 0,
    paymentTerms: row.paymentTerms || 'Net 30',
    scopeOfWork: row.scopeOfWork || '',
    services: Array.isArray(row.services) ? row.services : [],
    terms: row.terms || '',
    notes: row.notes || '',
    sentAt: row.sentAt || '',
    signedAt: row.signedAt || '',
    signedName: row.signedName || '',
    signedPosition: row.signedPosition || '',
    signatureImage: row.signatureImage || '',
    signatureText: row.signatureText || '',
    rejectReason: row.rejectReason || '',
    status: STATUS_FROM_API[row.status] || row.status || 'Draft',
    raw: row,
  };
}

function toIsoDate(value) {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function clip(value, max) {
  const text = String(value || '').trim();
  if (!text) return undefined;
  return text.length > max ? text.slice(0, max) : text;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function compactInput(input, keepEmpty = []) {
  const keep = new Set(keepEmpty);
  const next = {};
  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value === '' && !keep.has(key)) return;
    next[key] = value;
  });
  return next;
}

function buildContractInput(formData, statusLabel, sendSignatureEmail = false) {
  const apiStatus = STATUS_TO_API[statusLabel] || 'DRAFT';
  const staff =
    formData.staffRequired === '' || formData.staffRequired == null
      ? undefined
      : Number(formData.staffRequired);

  const serviceCost = parseMoney(formData.serviceCost ?? formData.totalContracted);
  const gstAmount = parseMoney(formData.gstAmount);
  const additionalCharges = parseMoney(formData.additionalCharges);
  const discount = parseMoney(formData.discount);
  const value =
    formData.totalValue != null
      ? parseMoney(formData.totalValue)
      : Math.max(0, serviceCost + gstAmount + additionalCharges - discount);

  const email = String(formData.email || '').trim();

  return compactInput({
    contractNumber: clip(formData.contractNumber, 80),
    bookingId: formData.bookingId || undefined,
    quotationVersionId: formData.quotationVersionId || undefined,
    quotationNumber: clip(formData.quotationNumber, 80),
    title: clip(formData.title || formData.serviceType, 255) || 'Untitled Contract',
    clientName: clip(formData.client || formData.contactPerson, 255) || 'Client',
    clientEmail: isValidEmail(email) ? email : undefined,
    companyName: clip(formData.companyName, 255),
    siteAddress: clip(formData.siteAddress, 500),
    contactPerson: clip(formData.contactPerson, 255),
    billingAddress: clip(formData.billingAddress, 500),
    phoneNumber: clip(formData.phoneNumber, 40),
    customerType: clip(formData.customerType, 80),
    serviceType: clip(formData.serviceType, 120),
    frequency: clip(formData.frequency, 80),
    siteSize: clip(formData.siteSize, 80),
    staffRequired: Number.isInteger(staff) ? staff : undefined,
    serviceCost,
    gstAmount,
    additionalCharges,
    discount,
    value,
    paymentReceived: parseMoney(formData.paymentReceived),
    paymentTerms: clip(formData.paymentTerms, 80),
    status: apiStatus,
    sendSignatureEmail: Boolean(sendSignatureEmail) || undefined,
    scopeOfWork: formData.scopeOfWork || '',
    services: Array.isArray(formData.services) ? formData.services : [],
    terms: formData.terms || '',
    notes: formData.notes || undefined,
    contractDate: toIsoDate(formData.contractDate),
    startDate: toIsoDate(formData.startDate),
    endDate: toIsoDate(formData.endDate),
    validUntil: toIsoDate(formData.validUntil || formData.endDate),
  }, ['scopeOfWork', 'terms']);
}

export { buildContractInput };

const CONTRACT_TAB_KEYS = STATUS_TABS.map((tab) => tab.key);

const Contract = () => {
  const { get, patch } = useAdminPageSearch();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [showContractForm, setShowContractForm] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const urlTab = get('tab');
  const activeTab = CONTRACT_TAB_KEYS.includes(urlTab) ? urlTab : 'ALL';
  const editId = get('edit');
  const viewId = get('view');
  const showAdd = get('action') === 'add';

  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await graphqlRequest(FIND_ALL_CLIENT_CONTRACTS);
      const rows = Array.isArray(res?.findAllClientContracts)
        ? res.findAllClientContracts
        : [];
      setContracts(rows.map(mapContractFromApi));
    } catch (err) {
      console.error('Failed to load contracts:', err);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const nextContractNumber = useMemo(() => {
    const year = new Date().getFullYear();
    const maxNum = contracts.reduce((max, c) => {
      const match = String(c.contractId || '').match(/CON-\d{4}-(\d+)/);
      const num = match ? parseInt(match[1], 10) : 0;
      return Math.max(max, num);
    }, 0);
    return `CON-${year}-${String(maxNum + 1).padStart(3, '0')}`;
  }, [contracts]);

  const handleAddOrUpdateContract = async (formData) => {
    const statusLabel = formData.status || formData.contractStatus || 'Draft';
    const wantsEmail = Boolean(formData.sendEmail);
    // Prefer id from the form payload so edit always updates (not create).
    const contractId = formData.id || selectedContract?.id || null;
    const isEdit = Boolean(contractId);

    if (wantsEmail && !String(formData.email || '').trim()) {
      toast.error('Client email is required to send for signature.');
      throw new Error('Client email is required');
    }

    const input = buildContractInput(formData, statusLabel, wantsEmail);

    try {
      if (isEdit) {
        await graphqlRequest(UPDATE_CLIENT_CONTRACT, {
          input: {
            id: contractId,
            ...input,
          },
        });
      } else {
        await graphqlRequest(CREATE_CLIENT_CONTRACT, { input });
      }
    } catch (err) {
      console.error('Contract save failed:', err);
      throw err;
    }

    if (wantsEmail) {
      const email = String(formData.email || '').trim();
      toast.success(
        `Signature email was sent to ${email}.\n\nIf you do not see it, check Spam / Promotions.`,
      );
    } else {
      toast.success(isEdit ? 'Contract updated.' : 'Contract created.');
    }

    await loadContracts();
    setEditMode(false);
    setSelectedContract(null);
  };

  const handleDelete = async (contract) => {
    if (!await appConfirmMessage(`Delete ${contract.contractId}? This cannot be undone.`)) {
      return;
    }
    try {
      if (contract.id) {
        await graphqlRequest(REMOVE_CLIENT_CONTRACT, { id: contract.id });
      }
      await loadContracts();
      if (selectedContract === contract) setSelectedContract(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete contract.');
    }
  };

  const openContract = async (contract, mode) => {
    if (!contract?.id) {
      toast.error(
        mode === 'view'
          ? 'This contract cannot be viewed (missing id).'
          : 'This contract cannot be edited (missing id).',
      );
      return;
    }

    setEditMode(mode === 'edit');
    setViewMode(mode === 'view');
    try {
      const res = await graphqlRequest(FIND_ONE_CLIENT_CONTRACT, {
        id: contract.id,
      });
      const full = mapContractFromApi(res?.findOneClientContract);
      if (!full?.id) {
        throw new Error('Contract not found');
      }
      setSelectedContract(full);
      setShowContractForm(mode !== 'view');
      if (mode === 'view') {
        patch({ view: full.id }, ['action', 'edit']);
      } else {
        patch({ edit: full.id }, ['action', 'view']);
      }
    } catch (err) {
      console.error(`Failed to load contract for ${mode}:`, err);
      setEditMode(false);
      setViewMode(false);
      setSelectedContract(null);
      toast.error(err?.message || 'Could not load contract details.');
    }
  };

  const handleEdit = (contract) => openContract(contract, 'edit');
  const handleView = (contract) => openContract(contract, 'view');

  useEffect(() => {
    if (showAdd) {
      setSelectedContract(null);
      setEditMode(false);
      setViewMode(false);
      setShowContractForm(true);
      return;
    }
    if (viewId) {
      if (selectedContract?.id === viewId && viewMode) return;
      handleView({ id: viewId });
      return;
    }
    if (!editId) return;
    if (selectedContract?.id === editId && showContractForm && editMode) return;
    handleEdit({ id: editId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdd, editId, viewId]);

  const clientOptions = useMemo(
    () =>
      Array.from(new Set(contracts.map((c) => c.client).filter(Boolean))).sort(),
    [contracts],
  );

  const serviceOptions = useMemo(
    () =>
      Array.from(
        new Set(contracts.map((c) => c.service).filter(Boolean)),
      ).sort(),
    [contracts],
  );

  const counts = useMemo(() => {
    const base = {
      ALL: contracts.length,
      Draft: 0,
      'Pending Signature': 0,
      'Change Requested': 0,
      Active: 0,
      EXPIRING: 0,
      Completed: 0,
      Declined: 0,
      Terminated: 0,
    };
    contracts.forEach((c) => {
      if (familyLooksActive(c)) base.Active += 1;
      else if (base[c.status] != null) base[c.status] += 1;
      if (isExpiringSoon(c)) base.EXPIRING += 1;
    });
    return base;
  }, [contracts]);

  const totalValue = useMemo(
    () => contracts.reduce((sum, c) => sum + parseMoney(c.total), 0),
    [contracts],
  );

  const filteredContracts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return contracts.filter((c) => {
      const matchTab = matchesStatusTab(c, activeTab);
      const matchClient = !clientFilter || c.client === clientFilter;
      const matchService = !serviceFilter || c.service === serviceFilter;
      const matchSearch =
        !term ||
        [c.contractId, c.client, c.service, c.status, c.clientEmail, c.quotationNumber]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term));
      return matchTab && matchClient && matchService && matchSearch;
    });
  }, [contracts, searchTerm, activeTab, clientFilter, serviceFilter]);

  return (
    <div className="con-page">
      <h1 className="con-title">Contract Dashboard</h1>

      <div className="con-toolbar">
        <div className="con-search">
          <FiSearch className="con-search-icon" size={16} />
          <input
            type="search"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="con-create-btn"
          onClick={() => {
            patch({ action: 'add' }, ['edit']);
          }}
        >
          <FiPlus size={16} />
          Add Contract
        </button>
      </div>

      <div className="con-kpi-grid">
        <div className="con-kpi-card">
          <div className="con-kpi-icon con-kpi-icon--total">
            <FiLayers />
          </div>
          <div>
            <p className="con-kpi-value">{counts.ALL}</p>
            <p className="con-kpi-label">Total Contracts</p>
          </div>
        </div>
        <div className="con-kpi-card">
          <div className="con-kpi-icon con-kpi-icon--active">
            <FiCheckCircle />
          </div>
          <div>
            <p className="con-kpi-value">{counts.Active}</p>
            <p className="con-kpi-label">Active</p>
          </div>
        </div>
        <div className="con-kpi-card">
          <div className="con-kpi-icon con-kpi-icon--pending">
            <FiEdit3 />
          </div>
          <div>
            <p className="con-kpi-value">{counts['Pending Signature']}</p>
            <p className="con-kpi-label">Pending Signature</p>
          </div>
        </div>
        <div className="con-kpi-card">
          <div className="con-kpi-icon con-kpi-icon--expiring">
            <FiClock />
          </div>
          <div>
            <p className="con-kpi-value">{counts.EXPIRING}</p>
            <p className="con-kpi-label">Expiring in 30 Days</p>
          </div>
        </div>
        <div className="con-kpi-card">
          <div className="con-kpi-icon con-kpi-icon--value">
            <FiDollarSign />
          </div>
          <div>
            <p className="con-kpi-value">{formatAUD(totalValue)}</p>
            <p className="con-kpi-label">Total Contract Value</p>
          </div>
        </div>
      </div>

      <div className="con-filter-bar">
        <div className="con-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`con-tab${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => patch({ tab: tab.key === 'ALL' ? '' : tab.key })}
            >
              {tab.label} ({counts[tab.key] ?? 0})
            </button>
          ))}
        </div>
        <div className="con-filters">
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option value="">All clients</option>
            {clientOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="">All services</option>
            {serviceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="con-table-wrap">
        <div className="con-table-scroll">
          <table className="con-table">
            <thead>
              <tr>
                <th>Contract</th>
                <th>Client</th>
                <th>Service</th>
                <th>Start</th>
                <th>End</th>
                <th>Value</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="con-empty">
                    Loading contracts…
                  </td>
                </tr>
              ) : filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="con-empty">
                    No contracts found.
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract) => {
                  const due = Math.max(
                    0,
                    parseMoney(contract.total) - parseMoney(contract.received),
                  );
                  return (
                    <tr key={contract.id || contract.contractId}>
                      <td>
                        <div>{contract.contractId}</div>
                        <div className="con-muted">
                          v{contract.versionNumber || 1}
                        </div>
                      </td>
                      <td>
                        <div>{contract.client}</div>
                        {contract.clientEmail ? (
                          <div className="con-muted">{contract.clientEmail}</div>
                        ) : null}
                      </td>
                      <td>{contract.service}</td>
                      <td>{formatDate(contract.billDate)}</td>
                      <td>{formatDate(contract.dueDate)}</td>
                      <td>
                        <div>{formatAUD(contract.total)}</div>
                        <div className="con-muted">Due {formatAUD(due)}</div>
                      </td>
                      <td>
                        <span
                          className={`con-status ${
                            STATUS_CLASS[
                              familyLooksActive(contract)
                                ? 'Active'
                                : contract.status
                            ] || ''
                          }`}
                        >
                          {listStatusLabel(contract)}
                        </span>
                        {contract.hasActiveVersion &&
                        contract.status !== 'Active' ? (
                          <div className="con-muted">
                            v{contract.versionNumber || 1} amendment
                          </div>
                        ) : null}
                        {contract.signedName && contract.status === 'Active' ? (
                          <div className="con-muted">
                            Signed by {contract.signedName}
                          </div>
                        ) : null}
                        {contract.rejectReason &&
                        (contract.status === 'Change Requested' ||
                          contract.status === 'Declined') ? (
                          <div className="con-muted con-status-note">
                            {contract.rejectReason}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="con-actions">
                          <button
                            type="button"
                            className="con-action-btn"
                            title="View"
                            onClick={() => handleView(contract)}
                          >
                            <FiEye size={14} />
                          </button>
                          <button
                            type="button"
                            className="con-action-btn"
                            title="Edit"
                            onClick={() => handleEdit(contract)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            type="button"
                            className="con-action-btn con-action-btn--danger"
                            title="Delete"
                            onClick={() => handleDelete(contract)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewMode && selectedContract?.id ? (
        <ContractViewModal
          contractId={selectedContract.id}
          contractNumber={selectedContract.contractId}
          contract={selectedContract}
          onClose={() => {
            setViewMode(false);
            setSelectedContract(null);
            patch({}, ['action', 'edit', 'view', 'proposalNumber']);
          }}
        />
      ) : null}

      {showContractForm && !viewMode && (
        <ContractForm
          key={
            selectedContract?.id
              ? `edit-${selectedContract.id}`
              : `new-${nextContractNumber}`
          }
          nextContractNumber={nextContractNumber}
          onClose={() => {
            setShowContractForm(false);
            setEditMode(false);
            setViewMode(false);
            setSelectedContract(null);
            patch({}, ['action', 'edit', 'view', 'proposalNumber']);
          }}
          onSubmit={handleAddOrUpdateContract}
          editData={editMode ? selectedContract : null}
        />
      )}
    </div>
  );
};

export default Contract;
